import { useTranslation } from '@msdk/common'
import { ViewColumnDefinition } from '@msdk/definitions'
import {
  ArrowDownIcon,
  ArrowUpIcon,
  Box,
  ButtonIcon,
  ButtonSecondary,
  COLORS,
  createComponentWrapperWithRef,
  Divider,
  FilterIcon,
  IconSelector,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MoreVertIcon,
  Popover,
  styled,
  THEME_CONST,
  Tooltip,
  uiTokens,
} from '@msdk/ui'
import React, { useState } from 'react'

import { CustomViewColumnDefinition, useDynamicViewContext } from './context'
import { FilterPanel } from './FilterPanel'
import { ManagementPanel } from './ManagementPanel'

const Container = styled('div')`
  display: flex;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  padding-right: 8px;
  background-color: white;
`

const Background = styled('div')`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
`

const StyledMuiButton = styled(ButtonIcon)<{ open?: boolean }>``

const StyledNovaButton = styled(ButtonIcon)<{ open?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: ${uiTokens.radius.small}px;
  transition: none;
  &:hover {
    border-radius: ${uiTokens.radius.small}px;
  }
  ${({ open }) =>
    open &&
    `
      background-color: ${uiTokens.uiControl.text.hover};
  `}
`
const StyledButton = createComponentWrapperWithRef(
  {
    mui: StyledMuiButton,
    nova: StyledNovaButton,
  },
  'StyledButton',
)

const StyledMenuItem = styled(MenuItem)`
  padding: 12px 16px;
`

const ResetTableButton = styled(ButtonSecondary)`
  border: none;
  margin-right: 4px;
  box-shadow: none;
  &:hover {
    border: none;
    box-shadow: none;
  }
  &.Mui-disabled {
    pointer-events: auto;
    background-color: transparent;
    border: none;
    box-shadow: none;
  }
  &.Mui-disabled:not(.closing):hover {
    cursor: not-allowed;
  }
`

interface Props {
  columnDef: ViewColumnDefinition
  menuAnchor: React.RefObject<HTMLElement>
  onToggle: (isOpen: boolean) => void
}

const TRANSITION_DURATION = THEME_CONST.standardTransitionDuration
const DELAY_FACTOR = 1
const DELAY_OFFSET = 50

function ColumnMenuComponent({ columnDef, menuAnchor, onToggle }: Props) {
  const t = useTranslation()

  const {
    isDefaultTable,
    filterColumns,
    order,
    orderBy,
    sortColumns,
    viewModel,
    toggleColumnVisibility,
    handleOrderChange,
    resetToDetaultTable,
  } = useDynamicViewContext()

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)
  const [menuOpen, setMenuOpen] = useState<boolean>(false)
  const [filterOpen, setFilterOpen] = useState<boolean>(false)
  const [managementOpen, setManagementOpen] = useState<boolean>(false)

  const isFilterColumn = !!filterColumns.find((c) => c.fieldName === columnDef.fieldName)
  const isSortColumn = !!sortColumns.find((c) => c.fieldName === columnDef.fieldName)

  const handleMenuClick = (e: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(e.currentTarget)
    setMenuOpen(true)
    onToggle(true)
  }

  const handleMenuClose = () => {
    setMenuAnchorEl(null)
    setMenuOpen(false)
    setTimeout(() => onToggle(false), TRANSITION_DURATION * DELAY_FACTOR + DELAY_OFFSET)
  }

  const onSortASC = () => {
    handleMenuClose()
    handleOrderChange(columnDef.fieldName, 'asc')
  }

  const onSortDESC = () => {
    handleMenuClose()
    handleOrderChange(columnDef.fieldName, 'desc')
  }

  const onFilterOpen = () => {
    handleMenuClose()
    setFilterOpen(true)
  }

  const onFilterClose = () => {
    setFilterOpen(false)
  }

  const onManagementOpen = () => {
    handleMenuClose()
    setManagementOpen(true)
  }
  const onManagementClose = () => {
    handleMenuClose()
    setManagementOpen(false)
  }

  const onHideColumn = () => {
    handleMenuClose()
    toggleColumnVisibility(columnDef as CustomViewColumnDefinition)
  }

  const resetTable = () => {
    handleMenuClose()
    resetToDetaultTable()
  }

  const isFirstColumn =
    columnDef.fieldName === viewModel.columns[viewModel.columns.findIndex((c) => !c.hidden)].fieldName

  return (
    <Container className="more-button">
      <Background className="more-button--backround">
        <StyledButton open={menuOpen} onClick={handleMenuClick} size="small">
          <MoreVertIcon fontSize="small" />
        </StyledButton>
      </Background>
      <Menu anchorEl={menuAnchorEl} open={menuOpen} onClose={handleMenuClose} transitionDuration={TRANSITION_DURATION}>
        {isSortColumn && ((orderBy === columnDef.fieldName && order !== 'asc') || orderBy !== columnDef.fieldName) && (
          <StyledMenuItem onClick={onSortASC}>
            <ListItemIcon>
              <ArrowUpIcon />
            </ListItemIcon>
            <ListItemText>{t('LBL_TABLE_SORT_BY_ASC', 'Sort by ASC')}</ListItemText>
          </StyledMenuItem>
        )}
        {isSortColumn && ((orderBy === columnDef.fieldName && order !== 'desc') || orderBy !== columnDef.fieldName) && (
          <StyledMenuItem onClick={onSortDESC}>
            <ListItemIcon>
              <ArrowDownIcon />
            </ListItemIcon>
            <ListItemText>{t('LBL_TABLE_SORT_BY_DESC', 'Sort by DESC')}</ListItemText>
          </StyledMenuItem>
        )}
        {isFilterColumn && (
          <Box>
            {isSortColumn && <Divider />}
            <StyledMenuItem onClick={onFilterOpen}>
              <ListItemIcon>
                <FilterIcon />
              </ListItemIcon>
              <ListItemText>{t('LBL_TABLE_FILTER', 'Filter')}</ListItemText>
            </StyledMenuItem>
          </Box>
        )}
        {!isFirstColumn && (
          <Box>
            {(isFilterColumn || isSortColumn) && <Divider />}
            <StyledMenuItem onClick={onHideColumn}>
              <ListItemIcon>
                <IconSelector iconName="VisibilityOffOutlined" color={COLORS.primary.main} />
              </ListItemIcon>
              <ListItemText>{t('LBL_TABLE_HIDE', 'Hide column')}</ListItemText>
            </StyledMenuItem>
          </Box>
        )}

        <Box>
          {isFirstColumn && (isFilterColumn || isSortColumn) && <Divider />}
          <StyledMenuItem onClick={onManagementOpen}>
            <ListItemIcon>
              <IconSelector iconName="ViewColumnOutlined" color={COLORS.primary.main} />
            </ListItemIcon>
            <ListItemText>{t('LBL_TABLE_MANAGEMENT', 'Manage columns')}</ListItemText>
          </StyledMenuItem>
        </Box>

        <Box>
          <Divider />
          <Tooltip
            title={
              isDefaultTable
                ? t(
                    'LBL_TABLE_MANAGEMENT_RESET_TABLE_DISABLED_TOOLTIP',
                    'The table is in the default state. Nothing to reset',
                  )
                : t(
                    'LBL_TABLE_MANAGEMENT_RESET_TABLE_TOOLTIP',
                    'Reset filters, sorting and columns to the default state.',
                  )
            }
            placement="right"
            arrow={true}
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginTop: '8px' }}
            slotProps={{
              tooltip: {
                hidden: !menuOpen,
              },
            }}
          >
            <div>
              <ResetTableButton
                onClick={resetTable}
                disabled={isDefaultTable}
                className={menuOpen ? undefined : 'closing'}
              >
                {t('LBL_TABLE_MANAGEMENT_RESET_TABLE', 'Reset table')}
              </ResetTableButton>
            </div>
          </Tooltip>
        </Box>
      </Menu>
      {menuAnchor.current && (
        <Popover
          open={filterOpen}
          anchorEl={menuAnchor.current}
          onClose={onFilterClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <FilterPanel columnDef={columnDef} />
        </Popover>
      )}
      {menuAnchor.current && (
        <Popover
          open={managementOpen}
          anchorEl={menuAnchor.current}
          onClose={onManagementClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <ManagementPanel />
        </Popover>
      )}
    </Container>
  )
}

export const ColumnMenu = React.memo(ColumnMenuComponent)
